import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Scale, Save } from "lucide-react";

const PRESETS = [
  { value: "flsa",       label: "FLSA (Federal)",  description: "40h weekly OT threshold. No daily OT, no DT." },
  { value: "california", label: "California",      description: "8h daily OT, 12h daily DT, 40h weekly OT, 7th-day rule." },
  { value: "custom",     label: "Custom",          description: "Roll your own — set any threshold below." },
];

export default function OvertimePolicyPage() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [policy, setPolicy] = useState("flsa");
  const [custom, setCustom] = useState({});
  const [ptoOt, setPtoOt] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tenant/config/overtime");
      setCfg(data);
      setPolicy(data.overtime_policy || "flsa");
      setCustom({
        daily_ot_threshold_hours: data.custom.daily_ot_threshold_hours ?? "",
        daily_dt_threshold_hours: data.custom.daily_dt_threshold_hours ?? "",
        weekly_ot_threshold_hours: data.custom.weekly_ot_threshold_hours ?? 40,
        weekly_dt_threshold_hours: data.custom.weekly_dt_threshold_hours ?? "",
        seventh_day_rule_enabled: !!data.custom.seventh_day_rule_enabled,
        seventh_day_ot_after_hours: data.custom.seventh_day_ot_after_hours ?? 0,
        seventh_day_dt_after_hours: data.custom.seventh_day_dt_after_hours ?? 8,
      });
      setPtoOt(!!data.pto_counts_toward_ot);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const effective = useMemo(() => {
    if (policy === "flsa") return { daily_ot_threshold_hours: null, daily_dt_threshold_hours: null, weekly_ot_threshold_hours: 40, weekly_dt_threshold_hours: null, seventh_day_rule_enabled: false };
    if (policy === "california") return { daily_ot_threshold_hours: 8, daily_dt_threshold_hours: 12, weekly_ot_threshold_hours: 40, weekly_dt_threshold_hours: null, seventh_day_rule_enabled: true, seventh_day_ot_after_hours: 0, seventh_day_dt_after_hours: 8 };
    return {
      daily_ot_threshold_hours: num(custom.daily_ot_threshold_hours),
      daily_dt_threshold_hours: num(custom.daily_dt_threshold_hours),
      weekly_ot_threshold_hours: num(custom.weekly_ot_threshold_hours),
      weekly_dt_threshold_hours: num(custom.weekly_dt_threshold_hours),
      seventh_day_rule_enabled: !!custom.seventh_day_rule_enabled,
      seventh_day_ot_after_hours: num(custom.seventh_day_ot_after_hours) || 0,
      seventh_day_dt_after_hours: num(custom.seventh_day_dt_after_hours) || 8,
    };
  }, [policy, custom]);

  // Live preview: worker who worked 10, 9, 8, 8, 8 = 43h
  const preview = useMemo(() => runPreview(effective), [effective]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { overtime_policy: policy, pto_counts_toward_ot: ptoOt };
      if (policy === "custom") Object.assign(payload, {
        daily_ot_threshold_hours: num(custom.daily_ot_threshold_hours),
        daily_dt_threshold_hours: num(custom.daily_dt_threshold_hours),
        weekly_ot_threshold_hours: num(custom.weekly_ot_threshold_hours),
        weekly_dt_threshold_hours: num(custom.weekly_dt_threshold_hours),
        seventh_day_rule_enabled: !!custom.seventh_day_rule_enabled,
        seventh_day_ot_after_hours: num(custom.seventh_day_ot_after_hours),
        seventh_day_dt_after_hours: num(custom.seventh_day_dt_after_hours),
      });
      const { data } = await api.patch("/tenant/config/overtime", payload);
      setCfg(data);
      toast.success("Overtime policy saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-6" data-testid="overtime-policy-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Time admin</div>
        <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Overtime policy</h1>
        <p className="mt-2 text-muted-foreground">Choose a preset or roll a custom OT bracket. Applies at read time — no re-writes to historical entries.</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Scale className="h-5 w-5" /> Policy preset</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-3">
            {PRESETS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPolicy(p.value)}
                data-testid={`ot-preset-${p.value}`}
                className={`text-left rounded-xl border p-4 transition ${policy === p.value ? "border-secondary bg-secondary/5 ring-1 ring-secondary" : "hover:bg-accent/40"}`}
              >
                <div className="font-semibold text-primary">{p.label}</div>
                <p className="mt-1 text-sm text-muted-foreground">{p.description}</p>
              </button>
            ))}
          </div>

          {policy === "custom" && (
            <div className="grid gap-4 rounded-xl border p-4 md:grid-cols-2" data-testid="ot-custom-grid">
              <NumField label="Daily OT threshold (h)" value={custom.daily_ot_threshold_hours} onChange={(v) => setCustom({ ...custom, daily_ot_threshold_hours: v })} testid="ot-daily-ot" />
              <NumField label="Daily DT threshold (h)" value={custom.daily_dt_threshold_hours} onChange={(v) => setCustom({ ...custom, daily_dt_threshold_hours: v })} testid="ot-daily-dt" />
              <NumField label="Weekly OT threshold (h)" value={custom.weekly_ot_threshold_hours} onChange={(v) => setCustom({ ...custom, weekly_ot_threshold_hours: v })} testid="ot-weekly-ot" />
              <NumField label="Weekly DT threshold (h)" value={custom.weekly_dt_threshold_hours} onChange={(v) => setCustom({ ...custom, weekly_dt_threshold_hours: v })} testid="ot-weekly-dt" />
              <div className="flex items-center justify-between rounded-md border bg-muted/40 p-3">
                <Label>7th-day rule</Label>
                <Switch checked={!!custom.seventh_day_rule_enabled} onCheckedChange={(v) => setCustom({ ...custom, seventh_day_rule_enabled: v })} data-testid="ot-seventh-day" />
              </div>
              <NumField label="7th-day: OT after (h)" value={custom.seventh_day_ot_after_hours} onChange={(v) => setCustom({ ...custom, seventh_day_ot_after_hours: v })} testid="ot-seventh-day-ot" />
              <NumField label="7th-day: DT after (h)" value={custom.seventh_day_dt_after_hours} onChange={(v) => setCustom({ ...custom, seventh_day_dt_after_hours: v })} testid="ot-seventh-day-dt" />
            </div>
          )}

          <div className="flex items-center justify-between rounded-md border bg-muted/40 p-3">
            <div>
              <div className="font-medium">PTO counts toward OT</div>
              <div className="text-xs text-muted-foreground">Default: no. FLSA excludes paid leave from OT calc.</div>
            </div>
            <Switch checked={ptoOt} onCheckedChange={setPtoOt} data-testid="ot-pto-counts" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Live preview</CardTitle>
          <CardDescription>For a worker who worked <span className="font-semibold text-foreground">10h Mon · 9h Tue · 8h Wed · 8h Thu · 8h Fri (43h)</span> — under this policy:</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-4">
            <Preview label="REG" minutes={preview.reg} color="text-primary" />
            <Preview label="OT (1.5×)" minutes={preview.ot} color="text-amber-600" />
            <Preview label="DT (2×)" minutes={preview.dt} color="text-destructive" />
            <Preview label="Paid total" minutes={preview.reg + preview.ot + preview.dt} color="text-secondary" />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="ot-save-btn"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save policy"}</Button>
      </div>
    </div>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input type="number" step="0.5" value={value === null || value === undefined ? "" : value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

function Preview({ label, minutes, color }) {
  return (
    <div className="rounded-xl border p-4">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`mt-1 font-display text-2xl tabular-nums ${color}`}>{fmt(minutes)}</div>
    </div>
  );
}

function fmt(m) { const h = Math.floor(m / 60); const mm = m % 60; return mm === 0 ? `${h}h` : `${h}h ${mm}m`; }
function num(v) { if (v === "" || v === null || v === undefined) return null; const n = Number(v); return Number.isFinite(n) ? n : null; }

// Pure JS mirror of overtime.py — mon-fri {10,9,8,8,8}
function runPreview(cfg) {
  const days = [10, 9, 8, 8, 8].map((h) => h * 60);
  const dOt = cfg.daily_ot_threshold_hours != null ? cfg.daily_ot_threshold_hours * 60 : null;
  const dDt = cfg.daily_dt_threshold_hours != null ? cfg.daily_dt_threshold_hours * 60 : null;
  const wOt = cfg.weekly_ot_threshold_hours != null ? cfg.weekly_ot_threshold_hours * 60 : null;
  const wDt = cfg.weekly_dt_threshold_hours != null ? cfg.weekly_dt_threshold_hours * 60 : null;
  const rows = days.map((total) => {
    let reg = total, ot = 0, dt = 0;
    if (dOt != null) {
      reg = Math.min(total, dOt);
      let rem = total - reg;
      if (dDt != null) { const cap = Math.max(0, dDt - dOt); ot = Math.min(rem, cap); dt = rem - ot; }
      else { ot = rem; dt = 0; }
    }
    return { reg, ot, dt };
  });
  // Weekly reclass
  if (wOt != null) {
    let cum = 0;
    for (const r of rows) {
      const allow = Math.max(0, wOt - cum);
      const keep = Math.min(r.reg, allow);
      const reclass = r.reg - keep;
      cum += keep;
      r.reg = keep;
      r.ot += reclass;
    }
    if (wDt != null) {
      let cumOt = 0;
      for (const r of rows) {
        const allow = Math.max(0, (wDt - wOt) - cumOt);
        const keep = Math.min(r.ot, allow);
        const reclass = r.ot - keep;
        cumOt += keep;
        r.ot = keep;
        r.dt += reclass;
      }
    }
  }
  return rows.reduce((acc, r) => ({ reg: acc.reg + r.reg, ot: acc.ot + r.ot, dt: acc.dt + r.dt }), { reg: 0, ot: 0, dt: 0 });
}
